// src/app/layout/shell.component.ts
import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, inject, effect, signal } from '@angular/core';
import PullToRefresh from 'pulltorefreshjs';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { filter, finalize, lastValueFrom, of, catchError, switchMap, Subscription } from 'rxjs';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NotificationService } from '../shared/services/notification.service';
import { NotificationType } from '../models/notification.model';
import { shouldApplyLegacyAndroidSafeArea } from '../shared/utils/legacy-safe-area.util';

@Component({
  standalone: true,
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.less'],
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    MatBadgeModule,
    TranslateModule,
    MatProgressSpinnerModule
  ]
})
export class ShellComponent implements OnInit, AfterViewInit, OnDestroy {
  private _router = inject(Router);
  private _translate = inject(TranslateService);
  private _activatedRoute = inject(ActivatedRoute);
  private _notificationService = inject(NotificationService);

  @ViewChild('sidenav') sidenav!: MatSidenav;
  sideNavOpen = false;

  pageTitle = signal('');
  isRefreshing = signal(false);
  isAppLoading = signal(false);

  unreadCounts = signal<Record<number, number>>({});
  legacySafeAreaTopInset = shouldApplyLegacyAndroidSafeArea(window.navigator.userAgent) ? 24 : 0;

  private ptrInstance!: { destroy(): void };
  private _pushSub?: Subscription;
  private _pushTappedSub?: Subscription;

  private navigationEnd$ = this._router.events.pipe(
    filter(e => e instanceof NavigationEnd),
    takeUntilDestroyed()
  );

  private langChange$ = this._translate.onLangChange.pipe(
    takeUntilDestroyed()
  );

  private navigationSignal = toSignal(this.navigationEnd$);
  private langChangeSignal = toSignal(this.langChange$);

  constructor() {
    effect(() => {
      const nav = this.navigationSignal();
      if (nav) {
        this.sidenav?.close();
        this.updatePageTitle();
      }
    });

    effect(() => {
      const langChange = this.langChangeSignal();
      if (langChange) {
        this.initPullToRefresh();
        this.updatePageTitle();
      }
    });
  }

  ngOnInit(): void {
    this.initPullToRefresh();
    this.updatePageTitle();
    this.loadUnreadCounts();

    this._pushSub = this._notificationService.pushReceived$.subscribe(event => {
      console.log('[Shell] Push notification received (type:', event.type, '), refreshing unread counts...');
      this.loadUnreadCounts();
    });

    this._pushTappedSub = this._notificationService.pushTapped$.subscribe(event => {
      console.log('[Shell] Push notification tapped (type:', event.type, ', id:', event.notificationId, '), navigating to detail...');
      const route = this.getDetailRoute(event.type, event.notificationId);
      if (route) {
        this._router.navigate([route]);
      }
    });
  }

  private loadUnreadCounts(): void {
    this._notificationService.getUnreadCountsByType().subscribe({
      next: counts => this.unreadCounts.set(counts),
      error: () => {}
    });
  }

  getUnreadCount(type: NotificationType): number {
    return this.unreadCounts()[type] ?? 0;
  }

  private updatePageTitle() {
    let route = this._activatedRoute.root;
    let titleKey = '';

    while (route.firstChild) {
      route = route.firstChild;
      const t = route.snapshot.data['title'] as string || '';
      if (t) { titleKey = t; }
    }

    if (titleKey) {
      const translated = this._translate.instant(titleKey);
      this.pageTitle.set(translated);
    } else {
      this.pageTitle.set('');
    }
  }

  private initPullToRefresh() {
    this.ptrInstance?.destroy();

    const pullText = this._translate.instant('SIDENAV.PULL');
    const releaseText = this._translate.instant('SIDENAV.RELEASE');
    const refreshText = this._translate.instant('SIDENAV.REFRESH');

    this.ptrInstance = PullToRefresh.init({
      mainElement: 'mat-sidenav-content',
      triggerElement: 'mat-sidenav-content',
      distThreshold: 80,
      distMax: 120,
      distIgnore: 10,
      instructionsPullToRefresh: pullText,
      instructionsReleaseToRefresh: releaseText,
      instructionsRefreshing: refreshText,
      refreshTimeout: 500,
      shouldPullToRefresh: () => {
        const inner = document.querySelector<HTMLElement>('.shell-content');
        return !!inner && inner.scrollTop === 0;
      },
      onRefresh: () => window.location.reload()
    });
  }

  private handleRefresh(): void {
    this.isRefreshing.set(true);
    this.isAppLoading.set(true);

    const currentLang = this._translate.currentLang;
    sessionStorage.setItem('ptr-refreshing', 'true');
    sessionStorage.setItem('ptr-language', currentLang);
  }

  private async waitForAppToLoad(): Promise<void> {
    const loadTranslations$ = this._translate.get('SIDENAV.PULL').pipe(
      switchMap(() => {
        const savedLang = sessionStorage.getItem('ptr-language');
        if (savedLang && savedLang !== this._translate.currentLang) {
          return this._translate.use(savedLang).pipe(
            catchError((error) => {
              console.error('Error switching language:', error);
              return of(null);
            })
          );
        }
        return of(null);
      }),
      catchError((error) => {
        console.error('Error loading translations:', error);
        return of(null);
      }),
      finalize(() => {
        sessionStorage.removeItem('ptr-refreshing');
        sessionStorage.removeItem('ptr-language');
        this.isAppLoading.set(false);
        this.isRefreshing.set(false);
      })
    );

    await lastValueFrom(loadTranslations$);
  }

  ngAfterViewInit(): void {
    const wasRefreshing = sessionStorage.getItem('ptr-refreshing');
    if (wasRefreshing) {
      this.isAppLoading.set(true);
      this.isRefreshing.set(false);
    }
  }

  ngOnDestroy(): void {
    this.ptrInstance?.destroy();
    this._pushSub?.unsubscribe();
    this._pushTappedSub?.unsubscribe();
  }

  toggleSideNav(): void {
    this.sideNavOpen = !this.sideNavOpen;
  }

  private getDetailRoute(type: NotificationType, notificationId: string): string | null {
    switch (type) {
      case NotificationType.Transaction: return `/transaction/detail/${notificationId}`;
      case NotificationType.Msg:         return `/message/detail/${notificationId}`;
      case NotificationType.Table:       return `/table/detail/${notificationId}`;
      case NotificationType.Chart:       return `/chart/detail/${notificationId}`;
      default:
        console.warn('[Shell] Unknown notification type for tapped event:', type);
        return null;
    }
  }

  logout(): void {
    window.location.href = 'app://logout';
  }
}
